import { createAction, props } from "@ngrx/store";

import { OptionSelection } from "../../../shared/types/processing-options.types";
import {
  AnalysisResult,
  DocumentResultResponse,
  DocumentUpload,
  DocumentUploadResponse,
  NormalizedTranscription,
  ProcessAnalysisOptions,
  TableData,
  UploadSession,
} from "./document.model";

export const analyzeDocument = createAction(
  "[Document] Document Analyze",
  props<{ document: DocumentUpload }>()
);

export const analyzeDocumentFailure = createAction(
  "[Document] Document Analyze Failure",
  props<{ error: string }>()
);

export const analyzeDocumentSuccess = createAction(
  "[Document] Analyze Document Success",
  props<{ result: DocumentUploadResponse }>()
);

// v2 stepped upload flow: initialize (reserve SAS URL) → upload (to blob) →
// process (enqueue). After the user clicks proceed on step 1, the upload and
// process steps are triggered automatically by effects. resolvedOptions carries
// the model/extraction settings resolved at step 1 so the auto-process effect
// can dispatch processUpload without re-reading component state.
export const initializeUpload = createAction(
  "[Document] Initialize Upload",
  props<{ document: DocumentUpload; resolvedOptions: ProcessAnalysisOptions }>()
);

export const initializeUploadSuccess = createAction(
  "[Document] Initialize Upload Success",
  props<{ session: UploadSession }>()
);

export const initializeUploadFailure = createAction(
  "[Document] Initialize Upload Failure",
  props<{ error: string }>()
);

export const uploadToBlob = createAction("[Document] Upload To Blob");

export const uploadToBlobProgress = createAction(
  "[Document] Upload To Blob Progress",
  props<{ percent: number }>()
);

export const uploadToBlobSuccess = createAction(
  "[Document] Upload To Blob Success"
);

export const uploadToBlobFailure = createAction(
  "[Document] Upload To Blob Failure",
  // Azure returns the same "AuthenticationFailed" for an expired SAS and a
  // malformed one, so an upload failure can't be classified as expiry here — it
  // is always surfaced as a retryable error (unlike process 410, which is a
  // reliable server-side expiry signal).
  props<{ error: string }>()
);

export const processUpload = createAction(
  "[Document] Process Upload",
  props<{ options: ProcessAnalysisOptions }>()
);

export const processUploadFailure = createAction(
  "[Document] Process Upload Failure",
  // expired = the request window is gone (410); like the upload case, the
  // reducer locks the panel's actions in place since it can't be reused.
  props<{ error: string; expired?: boolean }>()
);

export const getSupportedFileTypes = createAction(
  "[Document] Get Supported File Types"
);

export const setSupportedFileTypes = createAction(
  "[Document] Supported File Types",
  props<{ supportedFileTypes: string[] }>()
);

export const getFileSizeLimit = createAction("[Document] Get File Size Limit");

export const setFileSizeLimit = createAction(
  "[Document] File Size Limit",
  props<{ maxFileSizeBytes: number }>()
);

export const documentResult = createAction(
  "[Document] Document Result",
  props<{ id: string }>()
);

export const documentResultFailure = createAction(
  "[Document] Document Result Failure",
  props<{ error: string }>()
);

export const documentResultSuccess = createAction(
  "[Document] Document Result Success",
  props<{ result: DocumentResultResponse }>()
);

export const documentConvertedPdfSuccess = createAction(
  "[Document] Document Converted PDF Success",
  props<{ pdfFile: Blob }>()
);

export const updateProcessingOption = createAction(
  "[Document] Update Processing Options",
  props<{ option: OptionSelection }>()
);

export const documentResultsBlobSuccess = createAction(
  "[Document] Document Results Blob Success",
  props<{ results: AnalysisResult }>()
);

export const documentResultsBlobFailure = createAction(
  "[Document] Document Results Blob Failure",
  props<{ error: string }>()
);

export const documentTablesDataSuccess = createAction(
  "[Document] Document Tables Data Success",
  props<{ tablesData: TableData[] }>()
);

export const documentTablesDataFailure = createAction(
  "[Document] Document Tables Data Failure",
  props<{ error: string }>()
);

export const documentTranscriptionSuccess = createAction(
  "[Document] Document Transcription Success",
  props<{ transcription: NormalizedTranscription }>()
);

export const documentTranscriptionFailure = createAction(
  "[Document] Document Transcription Failure",
  props<{ error: string }>()
);

export const documentAudioSuccess = createAction(
  "[Document] Document Audio Success",
  props<{ audioBlob: Blob }>()
);

export const documentAudioFailure = createAction(
  "[Document] Document Audio Failure",
  props<{ error: string }>()
);

export const resetState = createAction("[Document] Reset Document State");
